import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  DOCUMENT,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { ShepherdService } from 'angular-shepherd';
import {
  combineLatest,
  filter,
  Observable,
  pairwise,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { UserService } from '@core/api/api';
import { AuthService } from '@core/auth-service/auth.service';
import { DownloadService } from '@core/download.service';
import { LanguageService } from '@core/language-service/language.service';
import { LoadingService } from '@core/loading.service';
import { ResponsiveService } from '@core/responsive.service';

type LoginFailedError = {
  message: string;
  reason: string;
  type: string;
};

declare global {
  function zE(type: 'messenger', action: 'resetWidget', callback: () => void): void;
  function zE(
    type: 'messenger',
    action: 'loginUser',
    jwtCallback: (cb: (token: string) => void) => void,
    loginCallback?: (loginStatus: LoginFailedError | null) => void,
  ): void;
  function zE(type: 'messenger', action: 'logoutUser'): void;
  function zE(type: 'messenger', action: 'show'): void;
  function zE(type: 'messenger', action: 'open'): void;
  function zE(type: 'messenger', action: 'close'): void;
  function zE(type: 'messenger', action: 'hide'): void;
  function zE(type: 'messenger:on', action: 'open', onOpen: () => void): void;
  function zE(type: 'messenger:on', action: 'close', onClose: () => void): void;
  function zE(
    type: 'messenger:on',
    action: 'unreadMessages',
    onMessageCountChange: (count: number) => void,
  ): void;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class MobileAppComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatSidenav) sideNav!: MatSidenav;
  @ViewChild('downloadFileAnchor')
  downloadFileAnchor!: ElementRef<HTMLAnchorElement>;

  protected readonly responsiveService = inject(ResponsiveService);
  private _authService = inject(AuthService);
  private userService = inject(UserService);
  private languageService = inject(LanguageService);
  private loadingService = inject(LoadingService);
  private readonly shepherdService = inject(ShepherdService);
  private document = inject<Document>(DOCUMENT);
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  public isLoading$: Observable<boolean> = this.loadingService.isLoading$;
  public sideMenuOpen = false;
  public isZendeskEnabled = false;
  protected isChatOpened = signal(false);
  protected unreadMessagesCount = signal(0);

  constructor() {
    const domSanitizer = this.domSanitizer;

    this.initZendeskChat();

    this.matIconRegistry.addSvgIcon(
      'card-view',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/card-view.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'app_registration',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/app_registration.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'results',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/results.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'package',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/package-icon.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'parameter',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/einzel-icon.svg'),
    );

    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');

    this.initSideMenu();
  }

  ngOnInit() {
    //Subscription is needed to activate the stream
    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    combineLatest([this._authService.loggedInUser$, this.responsiveService.isMobilePortrait$])
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.isZendeskEnabled = false;
          zE('messenger', 'hide');
        }),
        filter(([user, isMobile]) => !isMobile && !!user),
        switchMap(() => this.userService.getZendeskStatus()),
      )
      .subscribe((zendeskStatus) => {
        const sideMenuOpen = localStorage.getItem('sideMenuOpen');
        if (!sideMenuOpen) {
          this.sideMenuOpen = true;
        } else {
          this.sideMenuOpen = JSON.parse(sideMenuOpen);
          if (this.sideMenuOpen) {
            this.sideNav.open();
          } else {
            this.sideNav.close();
          }
        }

        if (zendeskStatus.mayUseChat) {
          if (zendeskStatus.hasExternalId) {
            zE(
              'messenger',
              'loginUser',
              (jwtCallback) => {
                this.userService
                  .getZendeskToken()
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe((token) => jwtCallback(token));
              },
              (error) => {
                if (error) {
                  console.error('ZD login failed', error);
                }
              },
            );
          }

          this.isZendeskEnabled = true;
          zE('messenger', 'show');
        }
      });

    this._authService.userIsLoggedIn$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => {
          // when previous state true (logged in) and current state false (logged out)
          return prev && !curr;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        zE('messenger', 'logoutUser');
        if (this.shepherdService.isActive) {
          this.shepherdService.cancel?.();
        }

        this.handleUserLogout();
      });

    this.downloadService.initialize(this.downloadFileAnchor);
  }

  async toggleSideMenu() {
    const result = await this.sideNav.toggle();
    localStorage.setItem('sideMenuOpen', JSON.stringify(result === 'open'));
  }

  openZendeskChat() {
    zE('messenger', 'open');
  }

  closeZendeskChat() {
    zE('messenger', 'close');
  }

  private initZendeskChat() {
    if (typeof zE !== 'function') {
      console.info('Zendesk script was not loaded.');
      window.zE = (...args: unknown[]) => {};
    }

    zE('messenger', 'hide');
    zE('messenger:on', 'open', () => {
      this.isChatOpened.set(true);
    });
    zE('messenger:on', 'close', () => {
      this.isChatOpened.set(false);
    });
    zE('messenger:on', 'unreadMessages', (count) => {
      this.unreadMessagesCount.set(count);
    });
  }

  private initSideMenu() {
    try {
      this.sideMenuOpen = JSON.parse(localStorage.getItem('sideMenuOpen') ?? 'false');
    } catch (e) {
      console.error('Error while parsing sideMenuOpen from localStorage: ', e);
      this.sideMenuOpen = false;
    }
  }

  private handleUserLogout() {
    setTimeout(() => this.document.location.reload(), 10);
  }
}
